import React from 'react';
import { SimpleBarChart } from '@carbon/charts-react';
import { useChartTheme } from './useChartTheme';

interface CategoryBarChartProps {
  title: string;
  data: Array<{ label: string; value: number }>;
  valueAxisTitle?: string;
  categoryAxisTitle?: string;
  /** Money charts only — pass useMoney()'s formatCompact/format; left unset, values stay raw. */
  axisValueFormatter?: (value: number) => string;
  tooltipValueFormatter?: (value: number) => string;
}

/**
 * One labelled bar chart, reused wherever a dashboard shows "a value per named thing" —
 * fines per type, headcount per branch, absences per employee. The alternative was eight
 * files differing only in their title and axis labels.
 *
 * Callers resolve their own ids to names before passing `label`; this component deliberately
 * knows nothing about employees, branches or issue types.
 */
const CategoryBarChart = React.memo((props: CategoryBarChartProps) => {
  const chartTheme = useChartTheme();
  const axisValueFormatter = props.axisValueFormatter;
  return (
    <div className="dashboard__stat_widget">
      <SimpleBarChart
        data={props.data.map((row) => ({ group: row.label, value: row.value ?? 0 }))}
        options={{
          title: props.title,
          axes: {
            left: { title: props.categoryAxisTitle, mapsTo: 'group', scaleType: 'labels' as any },
            bottom: {
              title: props.valueAxisTitle,
              mapsTo: 'value',
              ...(axisValueFormatter
                ? { ticks: { formatter: (tick: number | Date) => axisValueFormatter(Number(tick)) } }
                : {}),
            },
          },
          ...(props.tooltipValueFormatter ? { tooltip: { valueFormatter: props.tooltipValueFormatter } } : {}),
          height: '400px',
          theme: chartTheme,
        }}
      />
    </div>
  );
});

export default CategoryBarChart;
